// src/components/SideDrawer.tsx

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeService } from '../contexts/RealtimeServiceContext';
import { useFabricService } from '../contexts/FabricServiceContext';
import { Button } from './button/Button';
import { ProgressBar } from './progress/ProgressBar';
import { Edit } from 'react-feather';
import { MdLibraryAdd, MdCloudDownload } from "react-icons/md";
import { RiSparkling2Line } from "react-icons/ri";
import { FaCheck, FaPencilAlt } from "react-icons/fa";
import { X } from 'react-feather';
import { TiCancel } from "react-icons/ti";
import { MdLayersClear } from "react-icons/md";
import { FaRegTrashCan } from "react-icons/fa6";
import { FilterDropdown } from './FilterDropdown';
import { resizeGrid } from '../utils/layoutUtils';
import { timeAgo } from '../utils/time';
import { Layout } from '../types/Layout';
import { VOICE_OPTIONS } from '../types/Voice';
import { GridControls } from './GridControls';
import { CustomSelect } from './CustomSelect';
import { OptionGroup } from '../types/Dropdown';
import { NumericInput } from './NumericInput';
import { Toggle } from './toggle/Toggle';
import './SideDrawer.scss';

interface SideDrawerProps {
  isOpen: boolean;
  eventFilterTypes: string[];
  conversationFilterTypes: string[];
  onLayoutChange: (layout: Layout) => void;
  currentLayout: Layout;
}

export const SideDrawer: React.FC<SideDrawerProps> = ({
  isOpen,
  eventFilterTypes,
  conversationFilterTypes,
  onLayoutChange,
  currentLayout
}) => {
  const { apiKey, resetApiKey, isLocalRelay, useFabricAuth } = useAuth();
  const [layout, setLayout] = useState(currentLayout);
  const [onlyBaseInstances, setOnlyBaseInstances] = useState(true)
  const [sessionCount, setSessionCount] = useState(0)
  const [scenarioCount, setScenarioCount] = useState(0)
  const [instanceOptionList, setInstanceOptionList] = useState<{ value: string; label: string; hasParent: boolean; created: Date; }[]>([])
  const prevOptionsLengthRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    voice, setVoice,
    temperature, setTemperature,
    isConnected, resetConversation, disconnectConversation
  } = useRealtimeService();

  const {
    isFabric, setIsFabric,
    isFabricConnected, disconnectFabric,
    fabricVoice, setFabricVoice,
    fabricTemperature, setFabricTemperature,
    useSystemTwo, setUseSystemTwo,
    modelOptions, model, setModel,
    instanceOptions, instance, setInstance,
    fileOptions, file, setFile,
    isFileUploading, uploadFile,
    replaceFile, downloadFile, deleteFile,
    generationProgress, isGeneratingInstance, generateInstance,
    deleteInstance,
    conversationFilters, setConversationFilters,
    eventFilters, setEventFilters
  } = useFabricService();

  const voiceLabeledOptions = VOICE_OPTIONS.map(option => ({
    value: option.value,
    label: `${option.name} [${option.gender}] - ${option.description}`
  }))

  const modelOptionList = modelOptions.map(option => ({
    value: option.model,
    label: option.model.toLowerCase(),
    attributes: { provider: option.provider }
  }))

  const modelProviders = Array.from(new Set(modelOptions.map(option => option.provider))).sort()
  const modelGroups: OptionGroup[] = modelProviders.map(provider => ({
    id: provider,
    label: provider.startsWith("Bedrock") ? "Bedrock" : provider,
    filter: (option) => option.attributes?.provider === provider || false
  }));

  useEffect(() => {
    const optionlist = instanceOptions.map(option => {
      const nameLength = 30
      const optionName = option.name.length > nameLength ? `${option.name.slice(0, nameLength)}...` : option.name

      return {
        value: option.id,
        label: `${optionName.toLowerCase()}\n${option.model_name.toLowerCase()} — ${timeAgo(option.created_at)}`,
        hasParent: option.parent_id ? true : false,
        created: option.created_at
      }
    })

    const filtedList = optionlist.filter(option => onlyBaseInstances ? !option.hasParent : true)
    filtedList.sort((a, b) => b.created.getTime() - a.created.getTime())
    setInstanceOptionList(filtedList)
  }, [instanceOptions])

  const fileOptionList = fileOptions.map(option => {
    const maxLength = 40
    const secondaryLabel = option.description || option.name
    const secondaryLabelClipped = secondaryLabel.length > maxLength ? `${secondaryLabel.slice(0, maxLength)}...` : secondaryLabel

    return {
      value: option.id,
      label: `${secondaryLabelClipped}\n${option.user} — ${option.agent} [AI]`.toLowerCase(),
      is_default: option.is_default
    }
  })

  const fileGroups: OptionGroup[] = [
    {
      id: 'user',
      label: 'Your Files',
      filter: (option) => !option.is_default
    },
    {
      id: 'default',
      label: 'Examples',
      filter: (option) => option?.is_default || false
    }
  ];

  // #####################################
  // Section 1: SETTINGS - RUN
  // #####################################

  useEffect(() => {
    const savedVoice = getVoice(localStorage.getItem('tmp::fabric_scenario_voice'));
    if (savedVoice.value != fabricVoice.value) {
      setFabricVoice(savedVoice)
    }
    else if (isConnected) {
      resetConversation()
    }
  }, [fabricVoice])

  const handleFabricVoiceChange = useCallback((voiceValue?: string) => {
    const newVoice = getVoice(voiceValue)
    localStorage.setItem('tmp::fabric_scenario_voice', newVoice.value);
    setFabricVoice(newVoice)
  }, []);

  useEffect(() => {
    const savedInstance = getInstance(localStorage.getItem('tmp::fabric_scenario_instance'));
    if (savedInstance?.id != instance?.id) {
      setInstance(savedInstance)
    }
    else if (isConnected) {
      if (prevOptionsLengthRef.current === 0 && instanceOptions.length > 0) {
        resetConversation()
      }
    }
    prevOptionsLengthRef.current = instanceOptions.length;
  }, [instance, instanceOptions])

  const handleInstanceChange = useCallback((instanceValue?: string) => {
    const newInstance = getInstance(instanceValue)
    localStorage.setItem('tmp::fabric_scenario_instance', instanceValue || '');
    setInstance(newInstance)
  }, [instanceOptions]);

  const getInstance = useCallback((value?: string | null) => {
    return instanceOptions.find(instance => instance.id === value)
  }, [instanceOptions]);

  // #####################################
  // Section 2: BUILDER
  // #####################################

  useEffect(() => {
    const savedFile = getFile(localStorage.getItem('tmp::fabric_scenario_file'));
    if (savedFile?.id != instance?.id) {
      setFile(savedFile)
    }
  }, [file, fileOptions])

  const handleFileChange = useCallback((fileValue?: string) => {
    const newFile = getFile(fileValue)
    localStorage.setItem('tmp::fabric_scenario_file', fileValue || '');
    setFile(newFile)
  }, [fileOptions]);

  const getFile = useCallback((value?: string | null) => {
    return fileOptions.find(file => file.id === value)
  }, [fileOptions]);

  const handleFileDelete = useCallback(async () => {
    if (!file?.id) return;

    try {
      await deleteFile(file.id);
    } catch (error) {
      console.error('Error deleting file:', error);
      // Add appropriate error handling/user feedback
    }
  }, [deleteFile, file]);

  const handleFileOperation = useCallback(async (
    event: React.ChangeEvent<HTMLInputElement>,
    operation: 'upload' | 'replace'
  ) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    // Validate file type
    if (!uploadedFile.name.endsWith('.yaml')) {
      console.error('Only YAML files are allowed');
      return;
    }

    try {
      if (operation === 'upload') {
        const newFile = await uploadFile(uploadedFile);
        // Select the newly created file
        if (newFile) {
          setFile(newFile);
          localStorage.setItem('tmp::fabric_scenario_file', newFile.id);
        }
      } else if (operation === 'replace' && file?.id) {
        await replaceFile(file.id, uploadedFile);
      }
    } catch (error) {
      console.error(`Error ${operation}ing file:`, error);
      // Add appropriate error handling/user feedback
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadFile, replaceFile, file, setFile]);

  const handleFileDownload = useCallback(async () => {
    if (!file?.id || !file?.name) return;

    try {
      await downloadFile(file.id, file.name);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Add appropriate error handling/user feedback
    }
  }, [downloadFile, file]);

  // #####################################
  // Section 3: VOICE MODE
  // #####################################

  useEffect(() => {
    const savedVoice = getVoice(localStorage.getItem('tmp::fabric_voice_mode_voice'));
    if (savedVoice.value != voice.value) {
      setVoice(savedVoice)
    }
    else if (isConnected) {
      resetConversation()
    }
  }, [voice])

  const handleVoiceChange = useCallback((voiceValue?: string) => {
    const newVoice = getVoice(voiceValue)
    localStorage.setItem('tmp::fabric_voice_mode_voice', newVoice.value);
    setVoice(newVoice)
  }, []);

  const getVoice = (value?: string | null) => {
    return VOICE_OPTIONS.find(voice => voice.value === value) || VOICE_OPTIONS[0]
  }

  useEffect(() => {
    const savedTemperature = Math.max(0.6, Number(localStorage.getItem('tmp::fabric_voice_mode_temperature')));
    if (savedTemperature != temperature) {
      setTemperature(savedTemperature)
    }
    else if (isConnected) {
      resetConversation()
    }
  }, [temperature])

  const handleTemperatureChange = useCallback((value: number) => {
    localStorage.setItem('tmp::fabric_voice_mode_temperature', value.toString());
    setTemperature(value)
  }, []);

  // #####################################
  // Section 4: MODELS
  // #####################################

  useEffect(() => {
    const savedModel = localStorage.getItem('tmp::fabric_model_id');
    if (savedModel && savedModel != model) {
      setModel(savedModel)
    }
    else if (isConnected) {
      resetConversation()
    }
  }, [model])

  const handleModelChange = useCallback((value?: string) => {
    if (!value) return
    localStorage.setItem('tmp::fabric_model_id', value);
    setModel(value)
  }, []);

  useEffect(() => {
    const savedTemperature = Math.max(0, Number(localStorage.getItem('tmp::fabric_model_temperature')));
    if (savedTemperature != fabricTemperature) {
      setFabricTemperature(savedTemperature)
    }
    else if (isConnected) {
      resetConversation()
    }
  }, [fabricTemperature])

  const handleFabricTemperatureChange = useCallback((value: number) => {
    localStorage.setItem('tmp::fabric_model_temperature', value.toString());
    setFabricTemperature(value)
  }, []);

  useEffect(() => {
    const savedSystemTwo = Boolean(localStorage.getItem('tmp::fabric_model_system_two'));
    if (savedSystemTwo != useSystemTwo) {
      setUseSystemTwo(savedSystemTwo)
    }
    else if (isConnected) {
      resetConversation()
    }
  }, [useSystemTwo])

  const handleUseSystemTwoChange = useCallback((value: boolean) => {
    localStorage.setItem('tmp::fabric_model_system_two', value.toString());
    setUseSystemTwo(value)
  }, []);

  // #####################################
  // Section 5: DISPLAY FILTERS
  // #####################################

  useEffect(() => {
    const savedEventFilters = localStorage.getItem('tmp::fabric_event_filters');
    const savedConversationFilters = localStorage.getItem('tmp::fabric_conversation_filters');

    if (savedEventFilters) {
      const parsedFilters = JSON.parse(savedEventFilters);
      setEventFilters(parsedFilters);
    }

    if (savedConversationFilters) {
      const parsedFilters = JSON.parse(savedConversationFilters);
      setConversationFilters(parsedFilters);
    }
  }, [setEventFilters, setConversationFilters]);

  const handleEventFilterChange = useCallback((filters: (string | undefined)[]) => {
    setEventFilters(filters);
    localStorage.setItem('tmp::fabric_event_filters', JSON.stringify(filters));
  }, [setEventFilters]);

  const handleConversationFilterChange = useCallback((filters: (string | undefined)[]) => {
    setConversationFilters(filters);
    localStorage.setItem('tmp::fabric_conversation_filters', JSON.stringify(filters));
  }, [setConversationFilters]);

  // #####################################
  // Section 6: LAYOUT
  // #####################################

  useEffect(() => {
    setLayout(currentLayout);
  }, [currentLayout]);

  const handleLayoutChange = (newLayout: Layout) => {
    setLayout(newLayout);
    onLayoutChange(newLayout);
  };

  const handleRowChange = (newRows: number) => {
    const newGrid = resizeGrid(layout.grid, newRows, layout.columns);
    const newLayout = { ...layout, rows: newRows, grid: newGrid };
    if ((layout.rows === 0 || layout.columns === 0) && (newLayout.rows !== 0 && newLayout.columns !== 0)) {
      newLayout.size = 33;
    }
    handleLayoutChange(newLayout);
  };

  const handleColumnChange = (newColumns: number) => {
    const newGrid = resizeGrid(layout.grid, layout.rows, newColumns);
    const newLayout = { ...layout, columns: newColumns, grid: newGrid };
    handleLayoutChange(newLayout);
  };

  const handleWidgetChange = (rowIndex: number, colIndex: number, widgetName: string | null) => {
    if (rowIndex === -1 || colIndex === -1) {
      handleLayoutChange({ ...layout, main: { size: layout.main?.size || 0, widgetName } });
      return;
    }
    const newGrid = [...layout.grid];
    newGrid[rowIndex].cells[colIndex] = {
      ...newGrid[rowIndex].cells[colIndex],
      widgetName
    };
    handleLayoutChange({ ...layout, grid: newGrid });
  };

  // #####################################
  // Section 7: APPLICATION
  // #####################################

  useEffect(() => {
    setSessionCount(instanceOptions.length - instanceOptionList.length)
    setScenarioCount(instanceOptionList.length)
  }, [instanceOptions, instanceOptionList])

  const removeLocalStorageWithPrefix = (prefix: string): void => {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  }

  const handleApplicationReset = () => {
    removeLocalStorageWithPrefix('tmp::fabric')
    window.location.reload()
  }

  const handleScenerioDeleteAll = useCallback(async () => {
    if (isConnected) {
      disconnectConversation()
      if (isFabricConnected) {
        disconnectFabric()
      }
    }
    deleteInstance()
  }, [isConnected])


  return (
    <div className={`side-drawer ${isOpen ? 'open' : ''}`}>
      <div className="drawer-content">
        <div className="section">
          <span className='content-title'>conversation settings</span>
          <div className="filter-dropdown-wrapper">
            <div className="button-row">
              <CustomSelect
                options={instanceOptionList}
                value={instance?.id}
                onChange={handleInstanceChange}
                placeholder="select instance"
              />
              <div className="button-small destructive">
                <Button
                  icon={FaRegTrashCan}
                  iconSize={20}
                  buttonStyle="flush"
                  onClick={() => { deleteInstance(instance?.id) }}
                  disabled={!instance || !model}
                />
              </div>
            </div>
            <CustomSelect
              options={voiceLabeledOptions}
              value={fabricVoice.value}
              onChange={handleFabricVoiceChange}
              placeholder="select alternative voice"
            />
          </div>
        </div>
        <div className="section">
          <span className='content-title'>builder configuration</span>
          <div className="filter-dropdown-wrapper">
            <div className="button-row">
              <CustomSelect
                options={fileOptionList}
                value={file?.id}
                onChange={handleFileChange}
                placeholder="select file"
                groups={fileGroups}
              />
              <div className="button-small destructive">
                <Button
                  icon={FaRegTrashCan}
                  iconSize={20}
                  buttonStyle="flush"
                  onClick={handleFileDelete}
                  disabled={!file || file.is_default}
                />
              </div>
            </div>
            <div className="button-row primary">
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFileOperation(e, fileInputRef.current?.getAttribute('data-operation') as 'upload' | 'replace')}
                accept=".yaml"
                style={{ display: 'none' }}
              />
              <Button
                icon={MdLibraryAdd}
                iconSize={20}
                iconPosition="start"
                label="new"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.setAttribute('data-operation', 'upload');
                    fileInputRef.current.click();
                  }
                }}
                disabled={isFileUploading}
              />
              <Button
                icon={FaPencilAlt}
                iconSize={16}
                iconPosition="start"
                label="replace"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.setAttribute('data-operation', 'replace');
                    fileInputRef.current.click();
                  }
                }}
                disabled={!file || file.is_default || isFileUploading}
              />
              <Button
                icon={MdCloudDownload}
                iconSize={20}
                iconPosition="start"
                label="download"
                onClick={handleFileDownload}
                disabled={!file}
              />
            </div>
            <div className='primary-button-row'>
              {isGeneratingInstance ? (
                <ProgressBar
                  timeRemaining={generationProgress.timeRemaining}
                  processedItems={generationProgress.processedItems}
                  totalItems={generationProgress.totalItems}
                  percentComplete={generationProgress.percentComplete}
                  size={16}
                />
              ) : (
                <Button
                  icon={RiSparkling2Line}
                  iconSize={24}
                  iconPosition="start"
                  label="generate"
                  onClick={generateInstance}
                  buttonStyle='action'
                  disabled={!file || !model}
                />
              )}
            </div>
          </div>
        </div>
        <div className="section">
          <span className='content-title'>model</span>
          <div className="filter-dropdown-wrapper">
            <CustomSelect
              options={modelOptionList}
              value={model}
              groups={modelGroups}
              onChange={handleModelChange}
              placeholder="select model"
            />
            <NumericInput
              label='temperature'
              value={fabricTemperature}
              minimum={0}
              maximum={1}
              increment={0.1}
              onValueChange={handleFabricTemperatureChange}
            />
            <div className="row">
              <span>system two</span>
              <Toggle
                defaultValue={useSystemTwo}
                labels={['System 1', 'System 2']}
                icons={[<X size={20} />, <FaCheck size={18} />]}
                values={[false, true]}
                onChange={(_, value) => handleUseSystemTwoChange(value)}
              />
            </div>
          </div>
        </div>
        <div className="section">
          <span className='content-title'>advanced voice mode</span>
          <div className="filter-dropdown-wrapper">
            <div className="row">
              <span>intelligence layer</span>
              <Toggle
                defaultValue={isFabric}
                labels={['OpenAI', 'Fabric']}
                icons={[
                  <img src="/openai-icon.svg" width={18} height={18} alt="OpenAI" />,
                  <img src="/amazon-icon.svg" width={18} height={18} alt="Fabric" />
                ]}
                values={[false, true]}
                onChange={(_, value) => {
                  if (isFabric === value) return
                  setIsFabric(value)
                }
                }
              />
            </div>
            <CustomSelect
              options={voiceLabeledOptions}
              value={voice.value}
              onChange={handleVoiceChange}
              placeholder="select voice"
            />
            <NumericInput
              label='temperature'
              value={temperature}
              minimum={0.6}
              maximum={1.2}
              increment={0.1}
              onValueChange={handleTemperatureChange}
            />
            {(!isLocalRelay && !useFabricAuth) && (
              <div className="row">
                <label>api key</label>
                <Button
                  icon={Edit}
                  iconPosition="end"
                  buttonStyle="flush"
                  label={`${apiKey.slice(0, 3)}...`}
                  onClick={resetApiKey}
                />
              </div>
            )}
          </div>
        </div>
        <div className="section">
          <span className='content-title'>display filters</span>
          <div className="filter-dropdown-wrapper">
            <FilterDropdown
              filterType="conversation"
              options={conversationFilterTypes.map(filter => ({ label: filter, value: filter }))}
              selectedFilters={conversationFilters}
              onFilterChange={handleConversationFilterChange}
            />
            <FilterDropdown
              filterType="event"
              options={eventFilterTypes.map(filter => ({ label: filter, value: filter }))}
              selectedFilters={eventFilters}
              onFilterChange={handleEventFilterChange}
            />
          </div>
        </div>
        <div className="section">
          <span className='content-title'>layout</span>
          <GridControls
            layout={layout}
            onRowChange={handleRowChange}
            onColumnChange={handleColumnChange}
            onWidgetChange={handleWidgetChange}
          />
        </div>
        <div className="section">
          <span className='content-title'>application</span>
          <div className="filter-dropdown-wrapper destructive">
            <Button
              icon={TiCancel}
              iconSize={20}
              iconPosition="start"
              label="reset local presets"
              onClick={handleApplicationReset}
            />
            <Button
              icon={MdLayersClear}
              iconPosition="start"
              label={sessionCount === 0 ? 'no sessions available' : `clear ${sessionCount} sessions`}
              disabled={sessionCount === 0}
              onClick={() => { deleteInstance('sessions') }}
            />
            <Button
              icon={FaRegTrashCan}
              iconPosition="start"
              label={scenarioCount === 0 ? 'no instances available' : `delete ${scenarioCount} instances`}
              disabled={scenarioCount === 0}
              onClick={handleScenerioDeleteAll}
            />
          </div>
        </div>
      </div>
    </div>
  );
};